import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import LegacyFALogo from '../components/ui/LegacyFALogo';
import Icon from '../components/ui/Icon';
import Avatar from '../components/ui/Avatar';
import Chip from '../components/ui/Chip';
import TimelineRow from '../components/ui/TimelineRow';

/* ── tiny inline helpers ───────────────────────────────── */
const Field = ({ label, hint, children }) => (
  <div>
    <div className="flex items-baseline justify-between mb-1.5">
      <label className="block text-[12px] font-medium text-ink-600">{label}</label>
      {hint && <span className="text-[12px] text-leaf-700 font-medium cursor-pointer hover:text-leaf-800">{hint}</span>}
    </div>
    {children}
  </div>
);

const ENVELOPE_STAGES = [
  { label: 'Sent',      meta: 'Apr 22', state: 'done' },
  { label: 'Viewed',    meta: 'Apr 22', state: 'done' },
  { label: 'Signing',   meta: '2/3',    state: 'active' },
  { label: 'Completed', meta: '—',      state: 'pending' },
];

const LoginPage = () => {
  const [isRegister, setIsRegister] = useState(false);
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [name,     setName]     = useState('');
  const [company,  setCompany]  = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);
  const navigate = useNavigate();
  const { login, register } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (isRegister) {
        if (!name) { setError('Name is required'); setLoading(false); return; }
        const res = await register(name, email, password, company);
        if (res.success) navigate('/dashboard');
      } else {
        const res = await login(email, password);
        if (res.success) navigate('/dashboard');
      }
    } catch (err) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-paper-100">

      {/* ── Left — form panel ─────────────────────────────── */}
      <div className="flex-1 flex flex-col px-10 sm:px-14 py-10 min-h-screen">
        {/* Logo */}
        <div>
          <LegacyFALogo size="sm" />
        </div>

        <div className="flex-1 flex flex-col justify-center max-w-md">
          {/* Eyebrow */}
          <div className="text-[11px] font-mono uppercase tracking-[0.18em] text-leaf-700 mb-2">
            Advisor portal
          </div>

          <h1 className="font-display text-[36px] sm:text-[42px] leading-[1.05] font-bold text-ink-900 mb-3">
            {isRegister ? 'Create account.' : 'Welcome back.'}
          </h1>
          <p className="text-[15px] text-ink-500 leading-relaxed mb-8">
            {isRegister
              ? 'Set up your LegacyFA advisor account to start sending documents for signature.'
              : 'Sign in to review, prepare, and send documents to your clients for digital signature.'}
          </p>

          {/* Error */}
          {error && (
            <div className="mb-5 flex items-center gap-2.5 p-3.5 bg-flame-50 ring-1 ring-flame-100 rounded-xl text-flame-600 text-[13px]">
              <Icon name="alert" size={15} />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Register-only fields */}
            {isRegister && (
              <>
                <Field label="Full name">
                  <div className="relative">
                    <Icon name="user" size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-300" />
                    <input
                      type="text"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      placeholder="Jane Advisor"
                      required
                      className="w-full h-11 pl-10 pr-4 rounded-xl bg-white ring-1 ring-inset ring-ink-200 focus:ring-2 focus:ring-leaf-500 outline-none text-[14px] text-ink-800"
                    />
                  </div>
                </Field>
                <Field label="Company (optional)">
                  <input
                    type="text"
                    value={company}
                    onChange={e => setCompany(e.target.value)}
                    placeholder="LegacyFA Financial Advisory"
                    className="w-full h-11 px-4 rounded-xl bg-white ring-1 ring-inset ring-ink-200 focus:ring-2 focus:ring-leaf-500 outline-none text-[14px] text-ink-800"
                  />
                </Field>
              </>
            )}

            {/* Email */}
            <Field label="Email address">
              <div className="relative">
                <Icon name="mail" size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-300" />
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@legacyfa.com"
                  required
                  className="w-full h-11 pl-10 pr-4 rounded-xl bg-white ring-1 ring-inset ring-ink-200 focus:ring-2 focus:ring-leaf-500 outline-none text-[14px] text-ink-800"
                />
              </div>
            </Field>

            {/* Password */}
            <Field label="Password" hint={!isRegister ? 'Forgot?' : undefined}>
              <div className="relative">
                <Icon name="key" size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-ink-300" />
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder="••••••••••"
                  required
                  minLength={6}
                  className="w-full h-11 pl-10 pr-4 rounded-xl bg-white ring-1 ring-inset ring-ink-200 focus:ring-2 focus:ring-leaf-500 outline-none text-[14px] text-ink-800"
                />
              </div>
            </Field>

            {/* Submit */}
            <button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-leaf-600 hover:bg-leaf-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold rounded-xl text-[15px] transition-colors flex items-center justify-center gap-2 mt-2"
            >
              {loading
                ? 'Please wait…'
                : isRegister
                  ? 'Create account'
                  : <><span>Sign in to LegacyFA</span><Icon name="arrowRight" size={16} /></>
              }
            </button>

            {!isRegister && (
              <>
                {/* Or divider */}
                <div className="flex items-center gap-3 py-1">
                  <div className="flex-1 h-px bg-ink-100" />
                  <span className="text-[11px] text-ink-400 font-medium uppercase tracking-wider">or</span>
                  <div className="flex-1 h-px bg-ink-100" />
                </div>

                {/* Google SSO (visual only) */}
                <button
                  type="button"
                  className="w-full h-11 bg-white hover:bg-paper-100 text-ink-800 font-medium rounded-xl text-[14px] ring-1 ring-ink-200 transition-colors flex items-center justify-center gap-2.5"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24">
                    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09Z"/>
                    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/>
                    <path fill="#FBBC04" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.07H2.18a11 11 0 0 0 0 9.87l3.66-2.84Z"/>
                    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.1 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.83C6.71 7.31 9.14 5.38 12 5.38Z"/>
                  </svg>
                  Continue with Google Workspace
                </button>
              </>
            )}
          </form>

          {/* Toggle register / login */}
          <div className="mt-6 text-center">
            <button
              onClick={() => { setIsRegister(!isRegister); setError(''); }}
              className="text-[13px] text-leaf-700 hover:text-leaf-800 font-medium"
            >
              {isRegister ? 'Already have an account? Sign in' : "Don't have an account? Create one"}
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="text-[12px] text-ink-400 mt-8">
          © LegacyFA Financial Advisory ·{' '}
          <button
            onClick={() => window.open('/api-docs', '_blank')}
            className="hover:text-ink-700"
          >
            API Docs
          </button>
          {' · '}
          <span className="hover:text-ink-700 cursor-pointer">Privacy</span>
          {' · '}
          <span className="hover:text-ink-700 cursor-pointer">Security</span>
        </div>
      </div>

      {/* ── Right — brand panel ────────────────────────────── */}
      <div
        className="hidden lg:flex flex-1 relative overflow-hidden flex-col"
        style={{ background: 'linear-gradient(145deg, #0F1B2E 0%, #1B2B4B 55%, #0B7A55 130%)' }}
      >
        {/* Dot pattern */}
        <div
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: 'radial-gradient(rgba(255,255,255,0.8) 1px, transparent 1px)',
            backgroundSize: '22px 22px',
          }}
        />
        {/* Glow */}
        <div
          className="absolute -right-40 top-1/4 w-[560px] h-[560px] rounded-full pointer-events-none"
          style={{ background: 'radial-gradient(circle, rgba(16,185,129,0.22) 0%, transparent 60%)' }}
        />

        <div className="relative h-full flex flex-col justify-between p-12 text-white">
          {/* Status pill */}
          <div className="flex items-center gap-2 text-[12px] font-mono uppercase tracking-[0.18em] text-leaf-300">
            <span className="w-1.5 h-1.5 rounded-full bg-leaf-400 animate-pulse" />
            Secured envelope in transit
          </div>

          {/* Envelope preview card */}
          <div className="relative">
            {/* Stacked ghost cards */}
            <div className="absolute -top-6 -left-4 rotate-[-3deg] w-[78%] h-40 bg-white/10 backdrop-blur rounded-xl ring-1 ring-white/15" />
            <div className="absolute -top-3 -left-2 rotate-[-1.2deg] w-[84%] h-48 bg-white/15 backdrop-blur rounded-xl ring-1 ring-white/20" />

            {/* Main card */}
            <div className="relative bg-white rounded-2xl shadow-float p-5 text-ink-800 max-w-md">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2.5">
                  <div className="w-9 h-9 rounded-lg bg-leaf-50 flex items-center justify-center text-leaf-600">
                    <Icon name="file" size={16} />
                  </div>
                  <div>
                    <div className="text-[13px] font-semibold text-ink-900">Chen Family Trust · Policy Amendment</div>
                    <div className="text-[11px] text-ink-400 tnum font-mono">ENV · 4A12-99B</div>
                  </div>
                </div>
                <Chip tone="leaf" icon="check">2 of 3 signed</Chip>
              </div>

              <div className="pt-3 border-t border-ink-100">
                <TimelineRow stages={ENVELOPE_STAGES} compact />
              </div>

              <div className="mt-3 flex items-center gap-2">
                <div className="flex -space-x-1.5">
                  <Avatar name="Wei Chen"   size={24} ring />
                  <Avatar name="Linda Chen" size={24} ring />
                  <Avatar name="Jason Lim"  size={24} ring />
                </div>
                <span className="text-[11px] text-ink-400">Awaiting Jason Lim (Witness)</span>
              </div>
            </div>
          </div>

          {/* Tagline */}
          <div>
            <div className="font-display text-[28px] leading-[1.15] font-semibold max-w-sm mb-2">
              Financial paperwork, signed with assurance.
            </div>
            <div className="text-[13px] text-white/60 max-w-sm leading-relaxed">
              SingPass-grade verification, audit trails, and bundled envelope signing — purpose-built for advisory teams.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
